import * as querystringify from 'querystringify';
import UrlParse from 'url-parse';

export type Query = Record<string, string>;
export type QueryParser = (query: string) => Query;
export type StringifyQuery = (query: Record<any, any>) => string;
export interface ParseUrl {
  (url: string, parser?: boolean | QueryParser): Url;
  (url: string, baseUrl?: string | object, parser?: boolean | QueryParser): Url;
}

/**
 * 储存一个 Url 中各个部分数据的结构
 * @see https://nodejs.org/api/url.html#url_url_strings_and_url_objects
 */
export interface Url extends UrlData, UrlMethods {}

/** @ignore */
export interface UrlData {
  readonly auth: string;
  readonly hash: string;
  readonly host: string;
  readonly hostname: string;
  readonly href: string;
  readonly origin: string;
  readonly password: string;
  readonly pathname: string;
  readonly port: string;
  readonly protocol: string;
  readonly query: Query;
  readonly slashes: boolean;
  readonly username: string;
}

/** @ignore */
export interface UrlMethods {
  set(part: Exclude<keyof UrlData, 'query' | 'protocol'>, value: string | number): this;
  set(part: 'query', value: Record<any, any>, parser?: QueryParser): this;
  set(part: 'protocol', value: string, removeDoubleSlash?: boolean): this;
  toString(stringify?: StringifyQuery): string;
}

/**
 * 解析 URL
 * @returns 返回解析后的 URL 对象 ([解析规则](https://nodejs.org/api/url.html#url_url_strings_and_url_objects))
 * @description 此函数来自第三方库 [url-parse](https://github.com/unshiftio/url-parse) ([source](https://github.com/unshiftio/url-parse/blob/master/index.js))
 * @example
 * ```js
 * parseUrl("http://www.qq.com/foo/bar?a=b");           // -> Url { host: "www.qq.com", pathname: "/foo/bar", query: "?a=b", ... }
 * parseUrl("/foo/bar?a=b", "http://www.qq.com/baz");   // -> Url { host: "www.qq.com", pathname: "/foo/bar", query: "?a=b", ... }
 * parseUrl("http://www.qq.com/foo/bar?a=b", true);     // -> Url { host: "www.qq.com", pathname: "/foo/bar", query: { a: "b" }, ... }
 * ```
 */
export const parseUrl: ParseUrl = UrlParse as any;

/**
 * 解析 urlencoded 格式的 Query 字符串
 *
 * `parseQuery` 直接使用 "&", "=" 分割字符串, 并进行 Url 解码, 但不处理在 Query 字符串中嵌套数组或对象的情况.
 * 如果不需要 Url 解码, 请使用 [[parseMap]]. 如果需要更复杂的转换, 请使用第三方库 [qs](https://github.com/ljharb/qs).
 * @returns 返回保存了解析结果的对象
 * @description 此函数来自第三方库 [querystringify](https://github.com/unshiftio/querystringify#qsparse) ([source](https://github.com/unshiftio/querystringify/blob/master/index.js#L43))
 * @example
 * ```js
 * parseQuery("a=1&b=&foo=a+b&emoji=%26%5E%26");   // -> { a: "1", b: "", foo: "a b", emoji: "&^&" }
 * ```
 */
export const parseQuery: QueryParser = querystringify.parse as any;

/**
 * 将对象编码成 urlencoded 格式的 Query 字符串
 *
 * `stringifyQuery` 将对象的键和值转换成字符串, 并用 Url 编码后, 用 "=", "&" 连接.
 * 如果不需要 Url 编码, 请使用 [[stringifyMap]]. 如果需要更复杂的转换, 请使用第三方库 [qs](https://github.com/ljharb/qs).
 * @returns 返回编码后的字符串，其中 `undefined`、`null`、`NaN` 值会被替换成空字符串
 * @description
 * 此函数来自第三方库 [querystringify](https://github.com/unshiftio/querystringify#qsstringify) ([source](https://github.com/unshiftio/querystringify/blob/master/index.js#L75))，
 * 但在类型定义中删除了它不常用的第二个参数
 * @example
 * ```js
 * stringifyQuery({ foo: "bar", bar: "baz" });          // -> "foo=bar&bar=baz"
 * stringifyQuery({ n: 1, b: true });                   // -> "n=1&b=true"
 * stringifyQuery({ null: null, undef: undefined });    // -> "null=&undef="
 * stringifyQuery({ o: {} });                           // -> "o=%5Bobject%20Object%5D"
 * stringifyQuery({ emo: "(*^▽^*)", ch: "中文" });     // -> "emo=(*%5E%E2%96%BD%5E*)&ch=%E4%B8%AD%E6%96%87"
 * ```
 */
export const stringifyQuery: StringifyQuery = querystringify.stringify;

/**
 * 从 Url 上获取某个 Query 参数的值
 *
 * 如果要一次性从一个 Url 上获取好几个参数, 请使用 `parseUrl` 函
 * @param key 要获取的参数名
 * @param url Url, 没传时自动从浏览器环境中获取数
 * @returns 如果 Url 上有这个参数, 则返回该参数的值, 否则返回 `undefined`
 * @see [[parseUrl]]
 * @example
 * ```js
 * // location.href is http://www.example.org?foo=bar
 * getUrlParam("foo");                                // -> "bar"
 * getUrlParam("foo", "http://www.qq.com?foo=baz");   // -> "baz"
 * ```
 */
export function getUrlParam(key: string, url = window.location.href): string | undefined {
  return parseUrl(url, true).query[key];
}

/**
 * 从 urlencoded 格式的 Query 字符串上获取某个参数的值
 *
 * 如果要一次性从一个 Query 字符串上获取好几个参数, 请使用 `parseQuery` 函数
 * @param key 要获取的参数名
 * @param query Query 字符串, 没传时自动从浏览器环境中获取
 * @returns 如果参数存在, 则返回该参数的值, 否则返回 `undefined`
 * @see [[parseQuery]]
 * @example
 * ```js
 * // location.href is http://www.example.org?foo=bar
 * getQueryParam("foo");              // -> "bar"
 * getQueryParam("foo", "foo=baz");   // -> "baz"
 * ```
 */
export function getQueryParam(key: string, query = window.location.search.slice(1)): string | undefined {
  return parseQuery(query)[key];
}
